import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link, navigate } from "raviger";
import Moment from "react-moment";
import { Carousel } from "react-responsive-carousel";
import RangeSlider from "react-bootstrap-range-slider";
import {
  Button,
  Card,
  Col,
  Form as BootstrapForm,
  ListGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faMedkit,
  faCut,
  faEdit,
  faEnvelope,
  faLink,
  faMinusSquare,
  faTimes,
  faUserPlus,
  faFilePdf,
  faArrowAltCircleLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  faHandsHolding,
  faUserDoctorMessage,
  faClawMarks,
  faFolderMedical,
  faHomeHeart,
  faPhonePlus,
  faRightLeft,
  faSplit,
  faSkullCrossbones,
} from "@fortawesome/pro-solid-svg-icons";
import { faClosedCaptioningSlash } from "@fortawesome/pro-regular-svg-icons";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import * as Yup from "yup";
import { Formik } from "formik";

// Local components
import ClaimForm from "../reunification/ClaimForm";
import Scrollbar from "../components/Scrollbars";
import { AnimalDeleteModal } from "../components/Modals";
import Header from "../components/Header";
import History from "../components/History";
import AnimalCoverImage from "../components/AnimalCoverImage";
import ShelterlyPrintifyButton from "../components/ShelterlyPrintifyButton";
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from "../components/ActionsDropdown";

// Contexts and hooks
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from "../components/SystemError";
import { useLocationWithRoutes } from "../hooks";

// Utilities
import { printAnimalCareSchedule } from "./Utils";
import { getFileNameFromUrl, isImageFile } from "../utils/files";
import { useQueryParams } from "raviger";

function AnimalDetailsPublic() {
  const [queryParams] = useQueryParams();
  const {
      id = '',
  } = queryParams;
  const { dispatch } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);

  // Initial animal data
  const [data, setData] = useState({
    id,
    id_for_incident: "",
    name: "",
    species_string: "",
    sex: "",
    pcolor: "",
    scolor: "",
    microchip: "",
    front_image: null,
    has_owner: true,
    incident_slug:'',
    organization_name:'',
    organization_slug:''
  });

  const { getFullLocationFromPath } = useLocationWithRoutes();

  useEffect(() => {
    let unmounted = false;
    const source = axios.CancelToken.source();

    const fetchAnimalData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `/animals/api/public/${id}/`,
          { cancelToken: source.token }
        );

        if (!unmounted) {
          const animal = response.data;
          setData(animal);

          // Bias the address search widget toward the incident's location, same as authenticated pages.
          if (animal.incident_latitude && animal.incident_longitude) {
            dispatch({
              type: "SET_INCIDENT",
              data: {
                id: '', name: '', description: '', reunification_details: '', training: false,
                watchduty_map_id: '', caltopo_map_id: '', default_followup_days: '', has_claims: false,
                coordinates: [animal.incident_latitude, animal.incident_longitude],
              },
            });
          }

          let imageUrls = [animal.front_image, animal.side_image].filter(Boolean);

          if (Array.isArray(animal.extra_images)) {
            const extraImages = [];
            const extraDocuments = [];

            animal.extra_images.forEach((file) => {
              if (isImageFile(file)) {
                extraImages.push(file);
              } else {
                extraDocuments.push(file);
              }
            });

            imageUrls = imageUrls.concat(extraImages);
            setDocuments(extraDocuments);
          }

          setImages(imageUrls);
        }
      } catch (error) {
        if (!unmounted) setShowSystemError(true);
      } finally {
        if (!unmounted) setIsLoading(false);
      }
    };

    fetchAnimalData();

    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, dispatch, setShowSystemError]);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "300px" }}
      >
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <>
    <Header><span id="back" style={{cursor:'pointer'}} onClick={(e) => {navigate("/public/search?organization=" + data.organization_slug + "&incident=" + data.incident_slug)}} className="me-3" title={"/public/search"}><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{`Public Animal #${data.id_for_incident || ' - '}`}</Header>
      <Row className="g-3 g-md-4" style={{marginTop: "10px", marginLeft:"10px", marginRight:"10px"}}>
        {/* Left column */}
        <Col xs={12} md={6} className="d-flex">
          <Card className="border rounded d-flex" style={{ width: "100%" }}>
            <Card.Body style={{ marginTop: "-10px", marginBottom: "-6px" }}>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <h4 style={{ paddingTop: "5px" }}>Information</h4>
                {data.has_owner ?
                <OverlayTrigger
                  key={"disabled"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-disabled`}>
                      Claims may not be submitted for animals with owners.
                    </Tooltip>
                  }
                ><span>
                  <Button disabled variant="primary">
                    Submit a Claim
                  </Button></span>
                </OverlayTrigger>
                :
                <Button variant="primary" onClick={() => { setClaimSubmitted(false); setShowClaimForm(true); }}>
                  Submit a Claim
                </Button>}
              </div>
              <p className="phr pt-0 mt-1" />
              <ListGroup
                variant="flush"
                style={{ marginTop: "-13px", marginBottom: "-13px" }}
              >
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-6">
                      <b>Species:</b> {data.animal_count} {data.species_string}
                    </span>
                    <span className="col-6">
                      <b>Sex:</b> {data.sex || "Unknown"}
                    </span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-6">
                      <b>Organization:</b> {data.organization_name || "N/A"}
                    </span>
                    <span className="col-6">
                      <b>Primary Color:</b> {data.pcolor || "N/A"}
                    </span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-6">
                      <b>Incident:</b> {data.incident_name || "N/A"}
                    </span>
                    <span className="col-6">
                      <b>Secondary Color:</b> {data.scolor || "N/A"}
                    </span>
                  </div>
                </ListGroup.Item>
                { data.incident_reunification_details && ( 
                <ListGroup.Item className="text-capitalize">
                  <div className="row">
                    <span className="col-12">
                      <b>Reunification Details:</b> {data.incident_reunification_details || "N/A"}
                    </span>
                  </div>
                </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
        {/* Right column */}
        <Col xs={12} md={6} className="d-flex flex-column">
          <div
            className="public-animal-slide-container flex-grow-1 border rounded p-0"
            style={{ width: "100%", aspectRatio: "4 / 3", maxHeight: "345px" }}
          >
            {images.length < 1 ? (
              <Carousel
                className="public-animal-carousel-wrapper h-100"
                showThumbs={false}
                showStatus={false}
              >
                <AnimalCoverImage
                  animalSpecies={data.species_string_singular}
                  animalImageSrc={data.front_image}
                  customStyles={{
                    width: "100%",
                    padding: "40px",
                    height: "100%",
                    borderRadius: "3px",
                    objectFit: "contain",
                  }}
                />
              </Carousel>
            ) : (
              <Carousel
                className="public-animal-carousel-wrapper h-100"
                showThumbs={false}
                showStatus={false}
              >
                {images.map((image) => (
                  <div key={image} className="image-container">
                    <img src={image} alt="Animal" />
                  </div>
                ))}
              </Carousel>
            )}
          </div>
        </Col>
      </Row>
      <Modal show={showClaimForm} onHide={() => setShowClaimForm(false)} dialogClassName="wide-modal">
        <Modal.Header closeButton>
          <Modal.Title>Submit a Claim</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {claimSubmitted ? (
            <p style={{marginBottom:"0px"}}>Thank you! Your claim has been submitted and will be reviewed by shelter staff.</p>
          ) : (
            <ClaimForm animalId={data.id} onSuccess={() => setClaimSubmitted(true)} />
          )}
        </Modal.Body>
        {claimSubmitted ? (
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowClaimForm(false)}>Close</Button>
          </Modal.Footer>
          ) : (
            ""
          )}
      </Modal>
    </>
  );
}

export default AnimalDetailsPublic;
